import { useState } from 'react';
import { spreadTypes, spreadTypeCostScenario } from '../data';

const comparisonRows = [
  {
    metric: 'Spread on EURUSD',
    values: ['2.0 pips', '1.2 pips', '0.1 pips'],
    highlight: 2,
  },
  {
    metric: 'Commission per lot',
    values: ['$0', '$0', '$7.00'],
    highlight: -1,
  },
  {
    metric: 'Total cost per trade',
    values: ['$20.00', '$12.00', '$8.00'],
    highlight: 2,
  },
  {
    metric: 'Cost / 20 trades/mo',
    values: ['$400', '$240', '$160'],
    highlight: 2,
  },
  {
    metric: '% of $10K profit',
    values: ['4.0%', '2.4%', '1.6%'],
    highlight: 2,
  },
];

const typeLabels = ['Fixed', 'Floating', 'Raw'];

export default function SpreadTypesSection() {
  const [activeType, setActiveType] = useState(0);
  const current = spreadTypes[activeType];
  const cost = spreadTypeCostScenario[activeType];

  return (
    <section id="spread-types" className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[720px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Fixed, floating, or raw
            <br />
            — which spread do you get?
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Not all spreads are priced the same. Your broker's account type determines whether you see a locked number, a moving one, or the raw interbank rate plus commission.
          </p>
        </div>

        {/* ── Type selector ────────────────────────────────────────────── */}
        {/* Desktop: 3 equal wide tabs */}
        <div className="hidden sm:flex gap-3 mb-8 md:mb-10">
          {spreadTypes.map((type, i) => (
            <button
              key={type.type}
              onClick={() => setActiveType(i)}
              className={`text-left flex-1 px-5 py-4 rounded-card-sm border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                i === activeType
                  ? 'bg-primary-600 border-primary-500 text-white'
                  : 'bg-white border-background-200 hover:border-background-300 hover:bg-background-50'
              }`}
            >
              <div className={`text-[16px] font-bold mb-1 ${i === activeType ? 'text-white' : 'text-foreground-950'}`}>
                {type.type}
              </div>
              <div className={`text-[13px] ${i === activeType ? 'text-foreground-200' : 'text-foreground-500'}`}>
                {type.broker}
              </div>
            </button>
          ))}
        </div>

        {/* Mobile: compact chip row */}
        <div className="flex sm:hidden gap-2 mb-6 overflow-x-auto pb-1 scrollbar-none">
          {spreadTypes.map((type, i) => (
            <button
              key={type.type}
              onClick={() => setActiveType(i)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-full border text-[13px] font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                i === activeType
                  ? 'bg-primary-600 border-primary-500 text-white'
                  : 'bg-white border-background-200 text-foreground-600'
              }`}
            >
              <span>{type.type}</span>
              <span className={`ml-1.5 text-[11px] font-normal ${i === activeType ? 'text-primary-200' : 'text-foreground-400'}`}>
                {type.broker}
              </span>
            </button>
          ))}
        </div>

        {/* ── Detail panel ─────────────────────────────────────────────── */}
        {/* Desktop: 12-col grid */}
        <div className="hidden lg:grid grid-cols-12 gap-6 md:gap-8">
          {/* Left: profile */}
          <div className="col-span-5 bg-white rounded-card border border-background-200 p-6 md:p-8">
            <div className="text-[14px] font-semibold text-foreground-500 uppercase tracking-wide mb-5">
              {current.type} profile
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between py-3 border-b border-background-200">
                <span className="text-[14px] text-foreground-600">Typical spread</span>
                <span className="text-[16px] font-bold text-foreground-950">{current.spread}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-background-200">
                <span className="text-[14px] text-foreground-600">Commission</span>
                <span className="text-[16px] font-bold text-foreground-950">{current.commission}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-background-200">
                <span className="text-[14px] text-foreground-600">Execution model</span>
                <span className="text-[14px] font-semibold text-foreground-950">{current.execution}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-[14px] text-foreground-600">Best for</span>
                <span className="text-[13px] font-semibold text-primary-700 bg-primary-100 px-3 py-1 rounded-full">
                  {current.bestFor}
                </span>
              </div>
            </div>
            <div className="bg-background-100 rounded-card-sm border border-background-200 px-5 py-4">
              <p className="text-[14px] text-foreground-600 leading-relaxed">{current.desc}</p>
            </div>
          </div>

          {/* Middle: cost visual */}
          <div className="col-span-4 bg-white rounded-card border border-background-200 p-6 md:p-8">
            <div className="text-[14px] font-semibold text-foreground-500 uppercase tracking-wide mb-5">
              Cost on a 1-lot EURUSD trade
            </div>
            <div className="space-y-5 mb-6">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[13px] text-foreground-500">Spread cost</span>
                  <span className="text-[14px] font-semibold text-foreground-800">${cost.spreadCost}</span>
                </div>
                <div className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${(cost.spreadCost / cost.total) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[13px] text-foreground-500">Commission</span>
                  <span className="text-[14px] font-semibold text-foreground-800">${cost.commission}</span>
                </div>
                <div className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full rounded-full bg-secondary-500 transition-all duration-500" style={{ width: `${(cost.commission / cost.total) * 100}%` }} />
                </div>
              </div>
              <div className="pt-3 border-t border-background-200">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-bold text-foreground-800">Total cost</span>
                  <span className="text-[20px] font-bold text-foreground-950">${cost.total}</span>
                </div>
                <div className="h-3 rounded-full bg-background-200 overflow-hidden mt-2">
                  <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${(cost.total / 35) * 100}%` }} />
                </div>
                <p className="text-[12px] text-foreground-500 mt-1.5">{cost.totalNote}</p>
              </div>
            </div>
            <div className="bg-primary-50 rounded-card-sm border border-primary-200/60 px-4 py-3">
              <div className="text-[13px] font-semibold text-primary-800 mb-1">Volume breakpoint</div>
              <div className="text-[13px] text-primary-700 leading-relaxed">{cost.breakEven}</div>
            </div>
          </div>

          {/* Right: pros & cons */}
          <div className="col-span-3 bg-white rounded-card border border-background-200 p-6 md:p-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-check-line text-[16px] text-accent-600 w-5 h-5 flex items-center justify-center" />
                <span className="text-[14px] font-bold text-foreground-800 uppercase tracking-wide">Pros</span>
              </div>
              <ul className="space-y-2.5">
                {current.pros.map((pro) => (
                  <li key={pro} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 flex-shrink-0" />
                    <span className="text-[13px] text-foreground-600 leading-relaxed">{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-close-line text-[16px] text-secondary-600 w-5 h-5 flex items-center justify-center" />
                <span className="text-[14px] font-bold text-foreground-800 uppercase tracking-wide">Cons</span>
              </div>
              <ul className="space-y-2.5">
                {current.cons.map((con) => (
                  <li key={con} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 mt-2 flex-shrink-0" />
                    <span className="text-[13px] text-foreground-600 leading-relaxed">{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Mobile detail panel ────────────────────────────────────────── */}
        <div className="lg:hidden space-y-3">
          {/* Stats 2x2 grid */}
          <div className="bg-white rounded-card border border-background-200 p-5">
            <div className="text-[11px] font-semibold text-foreground-400 uppercase tracking-widest mb-4">
              {current.type} profile
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-background-50 rounded-xl p-3.5">
                <div className="text-[11px] text-foreground-500 mb-1">Typical spread</div>
                <div className="text-[15px] font-bold text-foreground-950 leading-tight">{current.spread}</div>
              </div>
              <div className="bg-background-50 rounded-xl p-3.5">
                <div className="text-[11px] text-foreground-500 mb-1">Commission</div>
                <div className="text-[15px] font-bold text-foreground-950 leading-tight">{current.commission}</div>
              </div>
              <div className="bg-background-50 rounded-xl p-3.5">
                <div className="text-[11px] text-foreground-500 mb-1">Execution</div>
                <div className="text-[13px] font-semibold text-foreground-900 leading-tight">{current.execution}</div>
              </div>
              <div className="bg-primary-50 rounded-xl p-3.5">
                <div className="text-[11px] text-primary-500 mb-1">Best for</div>
                <div className="text-[12px] font-semibold text-primary-800 leading-tight">{current.bestFor}</div>
              </div>
            </div>
            <p className="text-[13px] text-foreground-600 leading-relaxed">{current.desc}</p>
          </div>

          {/* Cost visual — compact */}
          <div className="bg-white rounded-card border border-background-200 p-5">
            <div className="text-[11px] font-semibold text-foreground-400 uppercase tracking-widest mb-4">
              Cost · 1-lot EURUSD
            </div>
            {/* Big total number */}
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-[40px] font-[800] text-foreground-950 leading-none">${cost.total}</span>
              <span className="text-[13px] text-foreground-500">per trade</span>
            </div>
            {/* Bars */}
            <div className="space-y-3 mb-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-foreground-500">Spread cost</span>
                  <span className="text-[13px] font-semibold text-foreground-800">${cost.spreadCost}</span>
                </div>
                <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${(cost.spreadCost / cost.total) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-foreground-500">Commission</span>
                  <span className="text-[13px] font-semibold text-foreground-800">${cost.commission}</span>
                </div>
                <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full rounded-full bg-secondary-500 transition-all duration-500" style={{ width: `${(cost.commission / cost.total) * 100}%` }} />
                </div>
              </div>
            </div>
            <div className="bg-primary-50 rounded-xl border border-primary-200/60 px-4 py-3">
              <div className="text-[11px] font-semibold text-primary-700 uppercase tracking-wide mb-1">Volume breakpoint</div>
              <div className="text-[12px] text-primary-700 leading-relaxed">{cost.breakEven}</div>
            </div>
          </div>

          {/* Pros & Cons — 2-col inline */}
          <div className="bg-white rounded-card border border-background-200 p-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-check-line text-[12px] text-accent-600" />
                  </span>
                  <span className="text-[12px] font-bold text-foreground-700 uppercase tracking-wide">Pros</span>
                </div>
                <ul className="space-y-2">
                  {current.pros.map((pro) => (
                    <li key={pro} className="flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent-500 mt-1.5 flex-shrink-0" />
                      <span className="text-[12px] text-foreground-600 leading-relaxed">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-close-line text-[12px] text-secondary-600" />
                  </span>
                  <span className="text-[12px] font-bold text-foreground-700 uppercase tracking-wide">Cons</span>
                </div>
                <ul className="space-y-2">
                  {current.cons.map((con) => (
                    <li key={con} className="flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-secondary-500 mt-1.5 flex-shrink-0" />
                      <span className="text-[12px] text-foreground-600 leading-relaxed">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom: Three-way comparison ─────────────────────────────── */}
        <div className="mt-8 md:mt-10">
          {/* Desktop table */}
          <div className="hidden sm:block bg-white rounded-card border border-background-200 overflow-hidden">
            <div className="px-6 md:px-8 py-5 border-b border-background-200">
              <h3 className="text-[16px] font-bold text-foreground-950">Three-way comparison</h3>
              <p className="text-[13px] text-foreground-500 mt-1">Same trade, three account types — cost breakdown side by side</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left text-[13px] font-semibold text-foreground-500 px-6 py-3.5 w-[200px]">Metric</th>
                    <th className="text-left text-[13px] font-semibold text-foreground-700 px-6 py-3.5 w-[180px]">Fixed Spread</th>
                    <th className="text-left text-[13px] font-semibold text-foreground-700 px-6 py-3.5 w-[180px]">Floating Spread</th>
                    <th className="text-left text-[13px] font-semibold text-primary-700 px-6 py-3.5 w-[180px]">Raw Spread</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="text-[13px] text-foreground-600 px-6 py-3.5">Spread on EURUSD</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">2.0 pips</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">1.2 pips</td>
                    <td className="text-[13px] font-semibold text-primary-700 px-6 py-3.5">0.1 pips</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="text-[13px] text-foreground-600 px-6 py-3.5">Commission per lot</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$0</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$0</td>
                    <td className="text-[13px] font-semibold text-primary-700 px-6 py-3.5">$7.00</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="text-[13px] text-foreground-600 px-6 py-3.5">Total cost per trade</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$20.00</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$12.00</td>
                    <td className="text-[13px] font-semibold text-primary-700 px-6 py-3.5">$8.00</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="text-[13px] text-foreground-600 px-6 py-3.5">Cost at 20 trades/month</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$400</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">$240</td>
                    <td className="text-[13px] font-semibold text-primary-700 px-6 py-3.5">$160</td>
                  </tr>
                  <tr>
                    <td className="text-[13px] text-foreground-600 px-6 py-3.5">Cost as % of $10K profit</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">4.0%</td>
                    <td className="text-[13px] font-semibold text-foreground-800 px-6 py-3.5">2.4%</td>
                    <td className="text-[13px] font-semibold text-primary-700 px-6 py-3.5">1.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: metric cards */}
          <div className="sm:hidden bg-white rounded-card border border-background-200 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-4 border-b border-background-200">
              <h3 className="text-[15px] font-bold text-foreground-950">Three-way comparison</h3>
              <p className="text-[12px] text-foreground-500 mt-0.5">Same 1-lot EURUSD trade across account types</p>
            </div>
            {/* Column labels */}
            <div className="grid grid-cols-3 bg-background-50 border-b border-background-200">
              {typeLabels.map((label, i) => (
                <div
                  key={label}
                  className={`px-3 py-2.5 text-center text-[12px] font-bold ${
                    i === 2 ? 'text-primary-700' : 'text-foreground-600'
                  }`}
                >
                  {label}
                </div>
              ))}
            </div>
            {/* Rows */}
            {comparisonRows.map((row, ri) => (
              <div key={row.metric} className={`${ri < comparisonRows.length - 1 ? 'border-b border-background-200' : ''}`}>
                <div className="px-4 py-2 bg-background-50/60 border-b border-background-100">
                  <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide">{row.metric}</span>
                </div>
                <div className="grid grid-cols-3">
                  {row.values.map((val, vi) => (
                    <div
                      key={vi}
                      className={`px-3 py-3 text-center text-[14px] font-bold ${
                        vi === row.highlight
                          ? 'text-primary-700 bg-primary-50/60'
                          : 'text-foreground-800'
                      }`}
                    >
                      {val}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}